import { Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'

import { AuthProvider, useAuth } from '@/features/auth/useAuth'
import { AppLayout } from '@/app/AppLayout'
import { HomePage } from '@/pages/HomePage'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { ErrorPage } from '@/pages/ErrorPage'
import { lazyPage } from '@/shared/lib/lazyPage'
import { Spinner } from '@/shared/ui/Spinner'

const DashboardPage = lazyPage(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const SettingsPage = lazyPage(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const ClassroomsPage = lazyPage(() =>
  import('@/pages/ClassroomsPage').then((m) => ({ default: m.ClassroomsPage })),
)
const ClassroomPage = lazyPage(() =>
  import('@/pages/ClassroomPage').then((m) => ({ default: m.ClassroomPage })),
)
const JoinByLinkPage = lazyPage(() =>
  import('@/pages/JoinByLinkPage').then((m) => ({ default: m.JoinByLinkPage })),
)
const TheoryPage = lazyPage(() =>
  import('@/pages/TheoryPage').then((m) => ({ default: m.TheoryPage })),
)
const PublicationPage = lazyPage(() =>
  import('@/pages/PublicationPage').then((m) => ({ default: m.PublicationPage })),
)
const MaterialPage = lazyPage(() =>
  import('@/pages/MaterialPage').then((m) => ({ default: m.MaterialPage })),
)
const MaterialEditPage = lazyPage(() =>
  import('@/pages/MaterialEditPage').then((m) => ({ default: m.MaterialEditPage })),
)
const TasksPage = lazyPage(() =>
  import('@/pages/TasksPage').then((m) => ({ default: m.TasksPage })),
)
const TaskPage = lazyPage(() => import('@/pages/TaskPage').then((m) => ({ default: m.TaskPage })))
const TaskEditPage = lazyPage(() =>
  import('@/pages/TaskEditPage').then((m) => ({ default: m.TaskEditPage })),
)
const TaskSetsPage = lazyPage(() =>
  import('@/pages/TaskSetsPage').then((m) => ({ default: m.TaskSetsPage })),
)
const TaskSetPage = lazyPage(() =>
  import('@/pages/TaskSetPage').then((m) => ({ default: m.TaskSetPage })),
)
const AssignmentsPage = lazyPage(() =>
  import('@/pages/AssignmentsPage').then((m) => ({ default: m.AssignmentsPage })),
)
const AssignmentNewPage = lazyPage(() =>
  import('@/pages/AssignmentNewPage').then((m) => ({ default: m.AssignmentNewPage })),
)
const AssignmentBuilderPage = lazyPage(() =>
  import('@/pages/AssignmentBuilderPage').then((m) => ({ default: m.AssignmentBuilderPage })),
)
const AssignmentPage = lazyPage(() =>
  import('@/pages/AssignmentPage').then((m) => ({ default: m.AssignmentPage })),
)
const AttemptPage = lazyPage(() =>
  import('@/pages/AttemptPage').then((m) => ({ default: m.AttemptPage })),
)
const AttemptResultPage = lazyPage(() =>
  import('@/pages/AttemptResultPage').then((m) => ({ default: m.AttemptResultPage })),
)
const ReviewsPage = lazyPage(() =>
  import('@/pages/ReviewsPage').then((m) => ({ default: m.ReviewsPage })),
)
const MyResultsPage = lazyPage(() =>
  import('@/pages/MyResultsPage').then((m) => ({ default: m.MyResultsPage })),
)
const AdminPage = lazyPage(() =>
  import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })),
)

function Root() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  )
}

/** Пускает дальше только вошедших. Пока сессия выясняется — показываем загрузку. */
function RequireAuth() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <Spinner label="Проверяем сессию" />
  if (!user) return <Navigate to="/вход" replace />
  return (
    <Suspense fallback={<Spinner />}>
      <AppLayout />
    </Suspense>
  )
}

/** Вошедшему пользователю страницы входа и регистрации не нужны. */
function GuestOnly() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <Spinner label="Проверяем сессию" />
  if (user) return <Navigate to="/кабинет" replace />
  return <Outlet />
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      { path: '/', element: <HomePage /> },
      {
        element: <GuestOnly />,
        children: [
          { path: '/вход', element: <LoginPage /> },
          { path: '/регистрация', element: <RegisterPage /> },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          { path: '/кабинет', element: <DashboardPage /> },
          { path: '/классы', element: <ClassroomsPage /> },
          { path: '/классы/:id', element: <ClassroomPage /> },
          { path: '/классы/:id/теория/:publicationId', element: <PublicationPage /> },
          // Ссылка-приглашение намеренно короткая: её диктуют и пересылают.
          { path: '/к/:code', element: <JoinByLinkPage /> },
          { path: '/теория', element: <TheoryPage /> },
          // «новый» идёт раньше «:id», иначе совпадёт с идентификатором.
          { path: '/теория/новый', element: <MaterialEditPage /> },
          { path: '/теория/:id', element: <MaterialPage /> },
          { path: '/теория/:id/правка', element: <MaterialEditPage /> },
          { path: '/задания', element: <TasksPage /> },
          // «новое» раньше «:id», иначе совпадёт с идентификатором.
          { path: '/задания/новое', element: <TaskEditPage /> },
          { path: '/задания/:id', element: <TaskPage /> },
          { path: '/задания/:id/правка', element: <TaskEditPage /> },
          { path: '/наборы', element: <TaskSetsPage /> },
          { path: '/наборы/:id', element: <TaskSetPage /> },
          { path: '/работы', element: <AssignmentsPage /> },
          // «новая» раньше «:id», иначе совпадёт с идентификатором.
          { path: '/работы/новая', element: <AssignmentNewPage /> },
          { path: '/работы/:id', element: <AssignmentPage /> },
          { path: '/работы/:id/конструктор', element: <AssignmentBuilderPage /> },
          { path: '/попытка/:id', element: <AttemptPage /> },
          { path: '/попытка/:id/итог', element: <AttemptResultPage /> },
          { path: '/проверка', element: <ReviewsPage /> },
          { path: '/результаты', element: <MyResultsPage /> },
          { path: '/админ', element: <AdminPage /> },
          { path: '/настройки', element: <SettingsPage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
